import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import {
  Alert, Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, IconButton, Stack, Tooltip, Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
  Create, Edit, NumberInput, SimpleForm, TextInput, Title, TopToolbar, required, useNotify
} from 'react-admin';
import { BackLink } from './back';
import { Link, useNavigate } from 'react-router-dom';
import { callAction, dataProvider } from './dataProvider';
import { FileImage } from './image';
import { FilejetImageInput } from './upload';

interface Reward {
  id: number;
  title: string;
  points: string;
  weight: number | null;
  description: string | null;
  fileIdentifier: string | null;
}

/**
 * The rewards catalogue, ordered by dragging.
 *
 * A hand-rolled list rather than a Datagrid: react-admin has no drag ordering,
 * and 29 rows do not justify a drag-and-drop library. Native HTML5 drag events
 * are enough at this size.
 *
 * The new order is sent as one list of ids and written in a single
 * transaction, not as a weight per row - a half-applied reorder would leave
 * the storefront showing a sequence nobody chose.
 */
export function LoyaltyList() {
  const notify = useNotify();
  const [rewards, setRewards] = useState<Reward[] | null>(null);
  const [dragging, setDragging] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Reward | null>(null);

  const load = () => {
    callAction('loyalty', 'GET' as never)
      .then(value => setRewards((value as { data: Reward[] }).data))
      .catch(() => notify('Odmeny sa nepodarilo načítať.', { type: 'warning' }));
  };

  useEffect(load, []);

  const move = (fromId: number, toId: number) => {
    if (rewards == null || fromId === toId) return;

    const next = [...rewards];
    const from = next.findIndex(reward => reward.id === fromId);
    const to = next.findIndex(reward => reward.id === toId);
    if (from < 0 || to < 0) return;

    const [moved] = next.splice(from, 1);
    if (moved == null) return;
    next.splice(to, 0, moved);
    setRewards(next);
  };

  const persist = async () => {
    if (rewards == null) return;
    setSaving(true);
    try {
      await callAction('loyalty/order', 'PUT', { ids: rewards.map(reward => reward.id) });
      notify('Poradie uložené.', { type: 'success' });
    } catch {
      notify('Poradie sa nepodarilo uložiť.', { type: 'warning' });
      // Re-read rather than keep an order the server rejected.
      load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (reward: Reward) => {
    setPendingDelete(null);
    try {
      await dataProvider.delete('loyalty', { id: reward.id, previousData: reward });
      notify(`Odmena „${reward.title}“ bola zmazaná.`, { type: 'info' });
      load();
    } catch {
      notify('Odmenu sa nepodarilo zmazať.', { type: 'warning' });
    }
  };

  if (rewards == null) return <Typography sx={{ p: 2 }}>Načítava sa…</Typography>;

  // "No order" means every weight is the same, not that they are all zero -
  // the default order is written as 1..n, so equality is the real test.
  const unordered = new Set(rewards.map(reward => reward.weight)).size <= 1;

  return (
    <Card sx={{ mt: 1 }}>
      <Title title="Vernostný program" />
      <TopToolbar>
        <Button component={Link} to="/loyalty/create" startIcon={<AddIcon />} size="small">
          Pridať odmenu
        </Button>
      </TopToolbar>
      <CardContent>
        {/* Only claim the order is unset when it actually is. Weights start at
            0 for every row, but the first drag writes a real sequence, and a
            banner that keeps saying "no order yet" afterwards is just wrong. */}
        {unordered ? (
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            Ťahaním nastavíte poradie, v ktorom odmeny uvidia zákazníci. Zatiaľ majú všetky rovnakú
            váhu, takže poradie ešte nie je určené — prvým potiahnutím vznikne.
          </Alert>
        ) : (
          <Alert severity="success" variant="outlined" sx={{ mb: 2 }}>
            Poradie zmeníte ťahaním. Uloží sa automaticky po pustení.
          </Alert>
        )}

        <Stack spacing={0.5}>
          {rewards.map(reward => (
            <Stack
              key={reward.id}
              direction="row"
              spacing={2}
              alignItems="center"
              draggable
              onDragStart={() => setDragging(reward.id)}
              onDragOver={event => {
                event.preventDefault();
                if (dragging != null) move(dragging, reward.id);
              }}
              onDragEnd={() => { setDragging(null); void persist(); }}
              sx={{
                p: 1,
                borderRadius: 1,
                cursor: 'grab',
                opacity: dragging === reward.id ? 0.4 : 1,
                bgcolor: dragging === reward.id ? 'action.selected' : 'transparent',
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              <DragIndicatorIcon fontSize="small" color="disabled" />
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 22, textAlign: 'right' }}>
                {reward.weight ?? '—'}
              </Typography>
              {/* A fixed box either way, so a reward without a picture does not
                  pull the whole row left and break the column alignment. */}
              <Box sx={{ width: 40, height: 40, flexShrink: 0 }}>
                <FileImage id={reward.fileIdentifier} alt={reward.title} size={40} />
              </Box>
              <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap>{reward.title}</Typography>
                {reward.description != null && (
                  <Typography variant="caption" color="text.secondary" noWrap>{reward.description}</Typography>
                )}
              </Stack>
              <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{reward.points} b.</Typography>
              <Tooltip title="Upraviť">
                <IconButton component={Link} to={`/loyalty/${reward.id}`} size="small" aria-label="Upraviť odmenu">
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Zmazať">
                <IconButton
                  size="small"
                  aria-label="Zmazať odmenu"
                  onClick={() => setPendingDelete(reward)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ))}
        </Stack>

        {saving && <Typography variant="caption" color="text.secondary">Ukladá sa poradie…</Typography>}
      </CardContent>

      {/* Deleting is not undoable and the row carries no warning of its own,
          so it asks first and names what it is about to remove. */}
      <Dialog open={pendingDelete != null} onClose={() => setPendingDelete(null)}>
        <DialogTitle>Zmazať odmenu?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Odmena „{pendingDelete?.title}“ zmizne z vernostného programu na webe. Túto akciu nie je
            možné vrátiť späť.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)}>Zrušiť</Button>
          <Button
            color="error"
            onClick={() => { if (pendingDelete != null) void remove(pendingDelete); }}
          >
            Zmazať
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

function RewardFields() {
  return (
    <>
      <TextInput source="title" label="Názov" validate={required()} fullWidth />
      <TextInput
        source="points"
        label="Body"
        validate={required()}
        helperText="Voľný text, nie číslo — niektoré odmeny majú „1000 a viac“."
      />
      <TextInput source="description" label="Popis" multiline rows={3} fullWidth />
      <FilejetImageInput
        source="fileIdentifier"
        label="Obrázok odmeny"
        helperText="PNG, JPEG, WebP, GIF alebo AVIF, do 10 MB."
      />
    </>
  );
}

export function LoyaltyEdit() {
  return (
    <Edit title="Odmena" mutationMode="pessimistic" redirect="list" actions={<TopToolbar sx={{ justifyContent: 'flex-start' }}><BackLink /></TopToolbar>}>
      <SimpleForm>
        <RewardFields />
        <NumberInput
          source="weight"
          label="Poradie"
          helperText="Nastavuje sa ťahaním v zozname."
        />
      </SimpleForm>
    </Edit>
  );
}

export function LoyaltyCreate() {
  const navigate = useNavigate();

  return (
    <Create
      title="Nová odmena"
      // Back to the list, which is the drag-ordered view; react-admin would
      // otherwise send the editor to a show page this resource does not have.
      mutationOptions={{ onSuccess: () => navigate('/loyalty') }}
    >
      <SimpleForm>
        <Alert severity="info" variant="outlined" sx={{ mb: 2, width: '100%' }}>
          Nová odmena sa zaradí na koniec zoznamu. Poradie potom upravíte ťahaním.
        </Alert>
        <RewardFields />
      </SimpleForm>
    </Create>
  );
}
